/**
 * RPR-6678: <rpr-video-player> -- inline Mux video player.
 *
 * Mounts the <mux-player> web component for a given Document. Fetches a
 * short-lived signed JWT via videoPlaybackService, then sets the
 * playback-id / playback-token attributes on the <mux-player> element.
 *
 * The matching <mux-player> web component is vendored at
 * portal/lib/thirdparty/mux-player/mux-player.3.13.0.js and bundled via
 * gulp; once it loads, it self-registers as the <mux-player> tag.
 *
 * Used by html_templates/directives/associated_documents.6768db85.html for video
 * attachments (preview-survey tab, response views, etc.) and by the
 * standalone video viewer view (Phase C3).
 *
 * Attributes (all dash-cased per AngularJS convention):
 *   - document-id          (required) Document.pk
 *   - document-name        (optional) used as metadata-video-title
 *   - mux-thumbnail-url    (optional) used as the player poster
 *   - mux-playback-id      (optional) saves a roundtrip when known
 *   - mux-processing-status (optional) drives the "still processing" placeholder
 */
app.directive('rprVideoPlayer',
  ['$q', 'videoPlaybackService', 'videoStatusPollerFactory', 'portalHelper',
    function ($q, videoPlaybackService, videoStatusPollerFactory, portalHelper) {
      // RPR-6752: friendly, non-technical explanations for a non-standard input.
      // The backend's `non_standard_input_reasons` keys (e.g. video_gop_size,
      // video_codec, video_edit_list) map to plain-language phrases so authors
      // understand WHY a healthy upload is taking longer -- it isn't stuck.
      const NON_STANDARD_REASON_TEXT = {
        video_gop_size: 'how its frames are grouped',
        video_codec: 'its video format',
        video_edit_list: 'embedded edit lists',
        audio_edit_list: 'embedded edit lists',
        unexpected_media_file_parameters: 'unusual encoding settings'
      };
      const NON_STANDARD_REASON_DEFAULT = 'its encoding';

      // Join the mapped phrases for whichever reasons the backend reported,
      // de-duped (both edit-list keys share one phrase) and order-preserving.
      function reasonTextFor(reasons) {
        const keys = (reasons && typeof reasons === 'object') ? Object.keys(reasons) : [];
        const phrases = [];
        keys.forEach(function (key) {
          const text = NON_STANDARD_REASON_TEXT[key] || NON_STANDARD_REASON_DEFAULT;
          if (!phrases.includes(text)) { phrases.push(text); }
        });
        if (!phrases.length) { return NON_STANDARD_REASON_DEFAULT; }
        if (phrases.length === 1) { return phrases[0]; }
        return phrases.slice(0, -1).join(', ') + ' and ' + phrases.at(-1);
      }

      // Seconds -> "about N minutes" (min 1 minute); null when not usable.
      // RPR-6752: delegates to the shared portalHelper formatter so the Document
      // Manager and the survey-preview status pill produce identical ETA copy.
      function formatEta(seconds) {
        return portalHelper.formatVideoEta(seconds);
      }

      // Compose the extended-processing subhint from the non-standard reasons,
      // appending an ETA phrase when the backend provides one.
      function buildNonStandardHint(data) {
        let hint = 'This video needs extended processing because of '
          + reasonTextFor(data.non_standard_input_reasons)
          + '. You can leave and come back — it\'ll be ready.';
        const eta = formatEta(data.estimated_seconds_remaining);
        if (eta) {
          hint += ' Estimated time remaining: ' + eta + '.';
        }
        return hint;
      }

      return {
        restrict: 'E',
        scope: {
          documentId: '@',
          documentName: '@',
          muxThumbnailUrl: '@',
          muxPlaybackId: '@',
          muxProcessingStatus: '@'
        },
        templateUrl: portalHelper.getUrlRev('html_templates/directives/rpr_video_player.e38f2ac2.html'),
        link: function (scope) {
          scope.state = 'loading';
          scope.errorMessage = null;
          // RPR-6741: trailing dots are supplied by the .mat-animated-ellipsis
          // ::after, so the processing copy ends without a period and the
          // motion reads as "still working". notReadyAnimated gates the
          // animation to the genuine processing placeholder (not the static
          // timed-out hint). notReadySubhint is an optional second line that
          // sets the "this takes a while, you can come back later" expectation
          // so authors don't sit and wait on a long large-video transcode.
          scope.notReadyMessage = 'Video is still being processed';
          scope.notReadyAnimated = false;
          scope.notReadySubhint = null;

          let poller = null;

          function mountWhenReady() {
            if (!scope.documentId) {
              scope.state = 'error';
              scope.errorMessage = 'Missing document id';
              return;
            }
            videoPlaybackService.getPlaybackToken(scope.documentId).then(function (token) {
              scope.playbackId = token.playbackId;
              scope.playbackToken = token.playbackToken;
              scope.drmLicenseToken = token.drmLicenseToken;
              scope.drmEnabled = token.drmEnabled;
              scope.state = 'ready';
            }, function (err) {
              scope.state = 'error';
              const status = err?.status;
              if (status === 404) {
                scope.errorMessage = 'Video is not available';
              } else if (status === 503) {
                // Backend signals the video service isn't configured (e.g. missing
                // Mux signing key) -- a deployment issue, not a transient failure.
                scope.errorMessage = 'Video is temporarily unavailable';
              } else {
                scope.errorMessage = 'Could not load video';
              }
            });
          }

          // RPR-6700: an evicted ("archived") video has no Mux asset; play the
          // retained original straight from S3 (admins never trigger a restore).
          function mountFromS3() {
            scope.state = 'loading';
            videoPlaybackService.getSourceUrl(scope.documentId).then(function (res) {
              scope.sourceUrl = res.sourceUrl;
              scope.state = 'source';
            }, function () {
              scope.state = 'error';
              scope.errorMessage = 'Video is not available';
            });
          }

          function onStatusUpdate(data) {
            const status = data?.status;
            if (status === 'ready') {
              scope.state = 'loading';
              mountWhenReady();
            } else if (status === 'archived') {
              mountFromS3();
            } else if (status === 'errored' || status === 'soft_deleted') {
              scope.state = 'error';
              scope.errorMessage = 'This video could not be processed.';
            } else if (status === 'timed_out') {
              scope.state = 'not_ready';
              // Static hint, not an active-progress indicator: no animated dots.
              scope.notReadyAnimated = false;
              if (data?.poll_failed) {
                // RPR-6923 review: the poller lost contact with our own status
                // endpoint -- nothing is known to be wrong with the video, and the
                // give-up calls stop(), so this verdict is final for the page
                // session. Match the other consumers' "status unavailable" story
                // (associated_documents' pill, portal_helper's replace toast)
                // instead of promising it "will be ready when you return".
                scope.notReadyMessage = 'Video status unavailable';
                scope.notReadySubhint = 'We could not check this video\'s processing status. Refresh the page to check again.';
              } else {
                scope.notReadyMessage = 'This is taking a little longer than usual';
                scope.notReadySubhint = 'Large videos can take several minutes to process (longer for very large files). You can leave this page and come back shortly — it will be ready when you return.';
              }
            } else {
              // RPR-6752: still processing. Surface live progress and, for a
              // non-standard input, a plain-language reason so authors know the
              // slow transcode is healthy rather than stuck. Backward-compatible:
              // older backends omit `progress`/`non_standard_input`, so the copy
              // falls back to the generic processing message + existing subhint.
              scope.state = 'not_ready';
              scope.notReadyAnimated = true;
              if (data?.progress?.percent != null) {
                scope.notReadyMessage = 'Processing video — ' + data.progress.percent + '%';
              } else {
                scope.notReadyMessage = 'Video is still being processed';
              }
              if (data?.non_standard_input) {
                scope.notReadySubhint = buildNonStandardHint(data);
              }
            }
          }

          // Asset already errored on initial load: terminal, don't poll.
          if (scope.muxProcessingStatus === 'errored' || scope.muxProcessingStatus === 'soft_deleted') {
            scope.state = 'error';
            scope.errorMessage = 'This video could not be processed.';
            return;
          }

          // Evicted/archived on initial load: play from S3, don't poll Mux.
          if (scope.muxProcessingStatus === 'archived') {
            mountFromS3();
            return;
          }

          // Not ready yet: show placeholder AND poll so it self-heals.
          if (scope.muxProcessingStatus && scope.muxProcessingStatus !== 'ready') {
            scope.state = 'not_ready';
            scope.notReadyAnimated = true;
            scope.notReadySubhint = 'Large videos can take several minutes to process (longer for very large files). You can carry on with other work and come back — it will be ready when you return.';
            if (scope.documentId) {
              poller = videoStatusPollerFactory.create({
                documentId: scope.documentId,
                onUpdate: onStatusUpdate
              });
              poller.start();
            }
            scope.$on('$destroy', function () { if (poller) poller.stop(); });
            return;
          }

          mountWhenReady();
        }
      };
    }
  ]
);
